import icon from "@assets/hockeyplanner-icon.svg";
import {
	Button,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { createAuthClient } from "better-auth/client";
import { emailOTPClient } from "better-auth/client/plugins";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FullPageLayout } from "@/frontend/components/FullPageLayout";
import { NotificationSnackbars } from "@/frontend/components/NotificationSnackbars";

const authClient = createAuthClient({
	basePath: "/api/auth",
	plugins: [emailOTPClient()],
});

export default function ResetPasswordView() {
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const token = params.get("token") || params.get("t") || "";
	const otp = params.get("otp") || "";
	const email = params.get("email") || "";

	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [passwordError, setPasswordError] = useState<string | null>(null);
	const [confirmError, setConfirmError] = useState<string | null>(null);
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [successMsg, setSuccessMsg] = useState<string | null>(null);

	const validate = (): boolean => {
		let ok = true;
		if (password.length < 8) {
			setPasswordError("Mindestens 8 Zeichen");
			ok = false;
		} else {
			setPasswordError(null);
		}
		if (password !== confirm) {
			setConfirmError("Passwörter stimmen nicht überein");
			ok = false;
		} else {
			setConfirmError(null);
		}
		return ok;
	};

	const handleReset = async () => {
		if (!token && !otp) {
			setErrorMsg("Ungültiger oder fehlender Token oder OTP.");
			return;
		}
		if (otp && !email) {
			setErrorMsg("Email fehlt für OTP.");
			return;
		}
		if (!validate()) return;
		try {
			if (otp) {
				await authClient.emailOtp.resetPassword({ email, otp, password });
			} else {
				await authClient.resetPassword({ newPassword: password, token });
			}
			setSuccessMsg("Passwort wurde gesetzt. Du kannst dich jetzt anmelden.");
			// Navigate immediately after success to avoid test flakiness and improve UX
			navigate("/", { replace: true });
		} catch (e: any) {
			setErrorMsg(e?.message ?? "Fehler beim Setzen des Passworts");
		}
	};

	return (
		<FullPageLayout>
			<img
				src={icon}
				alt="HockeyPlanner"
				style={{ width: 72, height: 72 }}
			/>
			<Stack spacing={0.5} alignItems="center">
				<Typography variant="h5" sx={{ fontWeight: 700 }}>
					Neues Passwort setzen
				</Typography>
				<Typography variant="body2" color="text.secondary" align="center">
					Gib dein neues Passwort ein.
				</Typography>
			</Stack>
			<form
							onSubmit={(e) => {
								e.preventDefault();
								handleReset();
							}}
						>
							<Stack spacing={2} sx={{ mt: 1, width: "100%" }}>
								<TextField
									label="Neues Passwort"
									type="password"
									fullWidth
									value={password}
									onChange={(e) => {
										setPassword(e.target.value);
										if (passwordError) setPasswordError(null);
									}}
									error={!!passwordError}
									helperText={passwordError || ""}
								/>
								<TextField
									label="Passwort bestätigen"
									type="password"
									fullWidth
									value={confirm}
									onChange={(e) => {
										setConfirm(e.target.value);
										if (confirmError) setConfirmError(null);
									}}
									error={!!confirmError}
									helperText={confirmError || ""}
								/>
								<Button
									type="submit"
									variant="contained"
									size="large"
									disabled={!token && !otp}
								>
									Passwort setzen
						</Button>
					</Stack>
				</form>
			<NotificationSnackbars
				errorMsg={errorMsg}
				successMsg={successMsg}
				onCloseError={() => setErrorMsg(null)}
				onCloseSuccess={() => setSuccessMsg(null)}
			/>
		</FullPageLayout>
	);
}
